import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useMusic } from '../contexts/MusicContext';
import { Button } from './ui/button';
import { Check, X, Music, Clock, Inbox, ChevronDown, ChevronUp, Zap, Megaphone } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Compact pending-requests inbox for the DJ Console.
 * Renders ABOVE the decks. Polls every 3s for new requests + shoutouts.
 * - `pending` song request (manual approve) → Approve button injects via injectNext()
 * - `pending` shoutout (manual approve) → Approve button PUTs /shoutouts/{id}/approve
 * - `approved` + `auto_accepted:true` song requests → auto-queued badge
 *
 * Feb 14 2026 — Rewritten to also fetch shoutouts (field report: guest
 * shoutouts were being submitted but never surfaced to the host for
 * approval on the Home screen). Also flipped the pending list to a
 * VERTICAL layout with scroll (was a horizontal x-scroll which wasted
 * the desktop right-column real estate).
 */
const RequestsInbox = () => {
  const { injectNext, refreshQueue } = useMusic();
  const [session, setSession] = useState(null);
  const [requests, setRequests] = useState([]);
  const [shoutouts, setShoutouts] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const pollRef = useRef(null);
  // Set of request ids we've already surfaced as "auto-queued" — used to
  // avoid double-firing the toast + refresh across poll ticks.
  const seenAutoIdsRef = useRef(new Set());

  const fetchSession = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/requests/sessions/active`);
      setSession(res.data || null);
      return res.data;
    } catch {
      setSession(null);
      return null;
    }
  }, []);

  const fetchRequests = useCallback(async (sid) => {
    if (!sid) return;
    // Fetch song requests + shoutouts in parallel — both feed the same
    // "pending host approval" inbox on the Home screen.
    try {
      const [reqRes, shoutRes] = await Promise.all([
        axios.get(`${API}/requests/sessions/${sid}/requests`),
        axios.get(`${API}/requests/sessions/${sid}/shoutouts`).catch(() => ({ data: [] })),
      ]);
      const list = reqRes.data || [];
      setRequests(list);
      setShoutouts(shoutRes.data || []);
      // Detect newly-arrived auto-accepted requests. When one lands, the
      // backend has already injected it into the playback queue — but the
      // DJ Console's visible queue is React state that only refreshes on
      // crossfade / manual actions. Without this poll-side refresh, the
      // DJ sees "Guest submitted" everywhere except in the up-next panel,
      // which was the exact production complaint.
      const freshAuto = list.filter(
        (r) => r.auto_accepted && r.status === 'approved' && !seenAutoIdsRef.current.has(r.id)
      );
      if (freshAuto.length > 0) {
        freshAuto.forEach((r) => seenAutoIdsRef.current.add(r.id));
        if (refreshQueue) refreshQueue().catch(() => {});
        const latest = freshAuto[0];
        toast.success(
          `${(latest.guest_name || 'Guest').slice(0, 20)} auto-queued: ${(latest.track?.name || 'a track').slice(0, 40)}`,
          { duration: 3500 }
        );
      }
    } catch (fetchErr) {
      console.warn('[RequestsInbox] fetch failed:', fetchErr?.response?.status, fetchErr?.message || fetchErr);
    }
  }, [refreshQueue]);

  useEffect(() => {
    (async () => {
      const s = await fetchSession();
      if (s?.id) {
        try {
          const [reqRes, shoutRes] = await Promise.all([
            axios.get(`${API}/requests/sessions/${s.id}/requests`),
            axios.get(`${API}/requests/sessions/${s.id}/shoutouts`).catch(() => ({ data: [] })),
          ]);
          const list = reqRes.data || [];
          list.forEach((r) => {
            if (r.auto_accepted && r.status === 'approved') {
              seenAutoIdsRef.current.add(r.id);
            }
          });
          setRequests(list);
          setShoutouts(shoutRes.data || []);
        } catch {
          await fetchRequests(s.id);
        }
      }
    })();
  }, [fetchSession, fetchRequests]);

  useEffect(() => {
    if (!session?.id) return undefined;
    pollRef.current = setInterval(() => fetchRequests(session.id), 3000);
    return () => clearInterval(pollRef.current);
  }, [session?.id, fetchRequests]);

  const pendingSongs = requests.filter((r) => r.status === 'pending');
  const pendingShouts = shoutouts.filter((s) => s.status === 'pending');
  // Unified pending list — song requests and shoutouts share the same
  // "host approval" surface. Kind field lets the row renderer branch.
  const pending = [
    ...pendingSongs.map((r) => ({ kind: 'song', ts: Date.parse(r.created_at || '') || 0, data: r })),
    ...pendingShouts.map((s) => ({ kind: 'shout', ts: Date.parse(s.created_at || '') || 0, data: s })),
  ].sort((a, b) => b.ts - a.ts);

  // Show the last 5 auto-queued in a dim secondary row so the DJ has
  // continuous visibility that guest-submitted songs actually landed.
  // Excludes tracks that have already been played (backend marks them
  // `played` when they finish — the DJ Console's `handleTrackEnded` fires
  // a `/requests/mark-played` POST to clear them from this list).
  // Also excludes any auto-approved row older than 24h — a defence-in-depth
  // filter alongside the server-side stale sweep in get_session_requests
  // (the signal to mark 'played' can miss on manual skips / restarts).
  const AUTO_STALE_MS = 24 * 60 * 60 * 1000;
  const recentAuto = requests
    .filter((r) => {
      if (!r.auto_accepted || r.status !== 'approved') return false;
      const ts = Date.parse(r.created_at || '');
      if (!Number.isNaN(ts) && Date.now() - ts > AUTO_STALE_MS) return false;
      return true;
    })
    .slice(0, 5);

  const handleApprove = async (req) => {
    setBusyId(req.id);
    try {
      await axios.put(`${API}/requests/${req.id}/approve`);
      setRequests((prev) => prev.map((r) => (r.id === req.id ? { ...r, status: 'approved' } : r)));
      if (req.track) await injectNext(req.track);
      toast.success('Approved — queued next');
    } catch {
      toast.error('Failed to approve');
    } finally {
      setBusyId(null);
    }
  };

  const handleReject = async (req) => {
    setBusyId(req.id);
    try {
      await axios.put(`${API}/requests/${req.id}/reject`);
      setRequests((prev) => prev.map((r) => (r.id === req.id ? { ...r, status: 'rejected' } : r)));
    } catch {
      toast.error('Failed to reject');
    } finally {
      setBusyId(null);
    }
  };

  const handleApproveShout = async (shout) => {
    setBusyId(shout.id);
    try {
      await axios.put(`${API}/shoutouts/${shout.id}/approve`);
      setShoutouts((prev) => prev.map((s) => (s.id === shout.id ? { ...s, status: 'approved' } : s)));
      toast.success('Shoutout live on the TV');
    } catch {
      toast.error('Failed to approve shoutout');
    } finally {
      setBusyId(null);
    }
  };

  const handleRejectShout = async (shout) => {
    setBusyId(shout.id);
    try {
      await axios.put(`${API}/shoutouts/${shout.id}/reject`);
      setShoutouts((prev) => prev.map((s) => (s.id === shout.id ? { ...s, status: 'rejected' } : s)));
    } catch {
      toast.error('Failed to reject shoutout');
    } finally {
      setBusyId(null);
    }
  };

  // Hidden when no live session at all.
  if (!session) return null;

  return (
    <div
      className="bg-white/[0.03] border border-white/10 rounded-2xl lg:rounded-3xl p-3 lg:p-4 backdrop-blur-xl flex flex-col min-h-0 h-full"
      data-testid="dj-requests-inbox"
    >
      <div className="flex items-center justify-between mb-2 lg:mb-3 flex-shrink-0">
        <div className="flex items-center gap-2">
          <Inbox className="w-4 h-4 lg:w-5 lg:h-5 text-[#FF007F]" />
          <h3 className="text-xs lg:text-sm font-extrabold text-white uppercase tracking-[0.18em]" style={{ fontFamily: 'Outfit' }}>
            Guest Requests
          </h3>
          {pending.length > 0 && (
            <span className="bg-[#FF007F] text-white text-[10px] font-bold px-2 py-0.5 rounded-full" data-testid="inbox-pending-count">
              {pending.length}
            </span>
          )}
          <span className="hidden sm:flex items-center gap-1 ml-2 text-[10px] text-[#39FF14] font-semibold tracking-widest">
            <span className="w-1.5 h-1.5 rounded-full bg-[#39FF14] animate-pulse" />
            LIVE
          </span>
        </div>
        <button
          onClick={() => setCollapsed((c) => !c)}
          className="text-gray-400 hover:text-white p-1 rounded transition-colors"
          data-testid="inbox-toggle-collapse"
          aria-label={collapsed ? 'Expand inbox' : 'Collapse inbox'}
        >
          {collapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
        </button>
      </div>

      {!collapsed && (
        pending.length === 0 ? (
          <>
            <div className="flex items-center gap-3 py-2 lg:py-3 text-gray-500 text-xs lg:text-sm">
              <Clock className="w-4 h-4" />
              No pending requests — relax and mix.
            </div>
            {recentAuto.length > 0 && (
              <div className="mt-1 border-t border-white/5 pt-2" data-testid="inbox-recent-auto">
                <p className="text-[9px] uppercase tracking-[0.2em] text-gray-500 mb-1.5 flex items-center gap-1">
                  <Zap className="w-3 h-3 text-[#39FF14]" /> Auto-queued (last {recentAuto.length})
                </p>
                <div className="flex flex-col gap-2">
                  {recentAuto.map((r) => (
                    <div
                      key={r.id}
                      className="flex items-center gap-2 bg-black/30 border border-[#39FF14]/20 rounded-lg px-2 py-1.5"
                      data-testid={`inbox-auto-${r.id}`}
                    >
                      {r.track?.album_image ? (
                        <img src={r.track.album_image} alt="" className="w-7 h-7 rounded object-cover flex-shrink-0" />
                      ) : (
                        <div className="w-7 h-7 rounded bg-white/5 flex items-center justify-center flex-shrink-0">
                          <Music className="w-3 h-3 text-gray-500" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-gray-300 text-[11px] font-semibold truncate">{r.track?.name || 'Unknown'}</p>
                        <p className="text-[#39FF14]/80 text-[9px] truncate">from {r.guest_name}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          // Feb 14 2026 — VERTICAL list with y-scroll (was flex + overflow-x-auto,
          // which wasted the desktop right-column real estate and hid rows off
          // the visible width). The container is flex-1 min-h-0 so the parent
          // (RequestsInbox root) controls height and this scrolls internally.
          <div className="flex flex-col gap-2 lg:gap-3 overflow-y-auto flex-1 min-h-0 -mx-1 px-1 pb-1" data-testid="inbox-pending-list">
            {pending.map((entry) => (
              entry.kind === 'shout' ? (
                <div
                  key={`s-${entry.data.id}`}
                  className="flex items-start gap-2 lg:gap-3 bg-black/40 border border-fuchsia-500/30 rounded-xl p-2 lg:p-3"
                  data-testid={`inbox-shoutout-${entry.data.id}`}
                >
                  <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-lg bg-fuchsia-500/10 flex items-center justify-center flex-shrink-0">
                    <Megaphone className="w-5 h-5 text-fuchsia-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-fuchsia-300 text-[10px] uppercase tracking-[0.18em] font-bold">Shoutout</p>
                    <p className="text-white text-xs lg:text-sm font-medium leading-snug break-words line-clamp-3">
                      "{entry.data.message}"
                    </p>
                    <p className="text-[#FF007F] text-[10px] font-semibold truncate mt-0.5">— {entry.data.guest_name || 'Anonymous'}</p>
                  </div>
                  <div className="flex flex-col gap-1 flex-shrink-0">
                    <Button
                      onClick={() => handleApproveShout(entry.data)}
                      disabled={busyId === entry.data.id}
                      className="bg-[#39FF14] hover:bg-[#2dd60f] text-black h-7 w-7 lg:h-8 lg:w-8 p-0 rounded-full"
                      title="Approve — send to TV"
                      data-testid={`inbox-shout-approve-${entry.data.id}`}
                    >
                      <Check className="w-3.5 h-3.5 lg:w-4 lg:h-4" />
                    </Button>
                    <Button
                      onClick={() => handleRejectShout(entry.data)}
                      disabled={busyId === entry.data.id}
                      variant="ghost"
                      className="text-[#FF3B3B] hover:bg-[#FF3B3B]/10 h-7 w-7 lg:h-8 lg:w-8 p-0 rounded-full"
                      title="Reject"
                      data-testid={`inbox-shout-reject-${entry.data.id}`}
                    >
                      <X className="w-3.5 h-3.5 lg:w-4 lg:h-4" />
                    </Button>
                  </div>
                </div>
              ) : (
                <div
                  key={`r-${entry.data.id}`}
                  className="flex items-center gap-2 lg:gap-3 bg-black/40 border border-white/10 rounded-xl p-2 lg:p-3"
                  data-testid={`inbox-request-${entry.data.id}`}
                >
                  {entry.data.track?.album_image ? (
                    <img src={entry.data.track.album_image} alt="" className="w-10 h-10 lg:w-12 lg:h-12 rounded object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-10 h-10 lg:w-12 lg:h-12 rounded bg-white/5 flex items-center justify-center flex-shrink-0">
                      <Music className="w-4 h-4 text-gray-500" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-xs lg:text-sm font-semibold truncate">{entry.data.track?.name || 'Unknown'}</p>
                    <p className="text-gray-400 text-[10px] lg:text-xs truncate">
                      {Array.isArray(entry.data.track?.artists) ? entry.data.track.artists.join(', ') : ''}
                    </p>
                    <p className="text-[#FF007F] text-[10px] font-semibold truncate">{entry.data.guest_name}</p>
                  </div>
                  <div className="flex flex-col gap-1 flex-shrink-0">
                    <Button
                      onClick={() => handleApprove(entry.data)}
                      disabled={busyId === entry.data.id}
                      className="bg-[#39FF14] hover:bg-[#2dd60f] text-black h-7 w-7 lg:h-8 lg:w-8 p-0 rounded-full"
                      title="Approve & queue next"
                      data-testid={`inbox-approve-${entry.data.id}`}
                    >
                      <Check className="w-3.5 h-3.5 lg:w-4 lg:h-4" />
                    </Button>
                    <Button
                      onClick={() => handleReject(entry.data)}
                      disabled={busyId === entry.data.id}
                      variant="ghost"
                      className="text-[#FF3B3B] hover:bg-[#FF3B3B]/10 h-7 w-7 lg:h-8 lg:w-8 p-0 rounded-full"
                      title="Reject"
                      data-testid={`inbox-reject-${entry.data.id}`}
                    >
                      <X className="w-3.5 h-3.5 lg:w-4 lg:h-4" />
                    </Button>
                  </div>
                </div>
              )
            ))}
          </div>
        )
      )}
    </div>
  );
};

export default RequestsInbox;
